import {
  CalendarOutlined,
  CheckCircleFilled,
  DownOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Button, Descriptions, Input, Modal, Select, Tag } from "antd";
import Skeleton from "components/UI/skeleton";
import dayjs from "dayjs";
import { useAntdMessage } from "hooks/useAntdMessage";
import {
  useMSScheduleDetail,
  useMSScheduleStatuses,
  usePatchMSScheduleStatus,
} from "queries/meetingSchedule";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { websiteIdsSelector } from "appredux/selectors";
import { ONLY_FOR_CRMKLEDO } from "utils/static";
import {
  formatStatusLabel,
  getScheduleRowStatusId,
  getStatusMeta,
} from "../AllSchedule/Table/scheduleRowFields";
import { REPORT_MIN_LENGTH, SCHEDULE_STATUS } from "../scheduleStatus";
import {
  resolveScheduleAdminGroupId,
  resolveScheduleContactId,
} from "../Schedule/scheduleDetailResolvers";
import { MEETING_APP, resolveAdminGroupAppId } from "../meetingAdminApp";
import ContactDetailLink from "../ContactDetailLink";
import AdminHebatReportFields from "./AdminHebatReportFields";
import MeetingReportWhatsappModal from "./MeetingReportWhatsappModal";

const { TextArea } = Input;
const { Option } = Select;

const STATUS = SCHEDULE_STATUS;

/** Status yang boleh dikonfirmasi → opsi Selesai / Tidak Hadir. */
const CONFIRMABLE_STATUS_IDS = [
  STATUS.NEW,
  STATUS.CONFIRMED,
  STATUS.KONFIRMASI_HADIR,
];

/** Label cadangan bila daftar status dari API belum termuat. */
const STATUS_FALLBACK_LABEL = {
  [STATUS.DONE]: { "id-ID": "Selesai", "en-US": "Done" },
  [STATUS.CANCELLED]: { "id-ID": "Dibatalkan", "en-US": "Cancelled" },
  [STATUS.NO_SHOW]: { "id-ID": "Tidak Hadir", "en-US": "No Show" },
};

const buildStatusOption = (statuses, id, locale) => {
  const meta = getStatusMeta(statuses, id);
  const label = meta
    ? formatStatusLabel(meta, locale)
    : STATUS_FALLBACK_LABEL[id]?.[locale] ??
      STATUS_FALLBACK_LABEL[id]?.["id-ID"] ??
      String(id);
  return { value: id, label, color: meta?.color ?? null };
};

const MeetingFinishedModal = ({ schedule, onClose, onReschedule }) => {
  const { t, i18n } = useTranslation();
  const { message } = useAntdMessage();
  const locale = i18n.language?.startsWith("en") ? "en-US" : "id-ID";

  const websiteId = useSelector(websiteIdsSelector);
  const isInternal = ONLY_FOR_CRMKLEDO.includes(websiteId);

  const scheduleId = schedule?.schedule_id ?? null;

  const { data: statuses = [] } = useMSScheduleStatuses(locale, {
    enabled: Boolean(scheduleId),
  });
  const {
    data: scheduleDetail,
    isLoading,
    isError,
  } = useMSScheduleDetail(scheduleId, {
    enabled: Boolean(scheduleId),
    retry: 1,
  });

  const patchStatusMutation = usePatchMSScheduleStatus();

  const [selectedStatusId, setSelectedStatusId] = useState(null);
  const [report, setReport] = useState("");
  const [adminHebat, setAdminHebat] = useState({
    appId: MEETING_APP.FINANCE,
    company: null,
  });
  // Pratinjau WhatsApp yang muncul setelah report tersimpan.
  const [waOpen, setWaOpen] = useState(false);
  const [waReport, setWaReport] = useState("");
  const [waAppId, setWaAppId] = useState(null);

  // Reset form tiap kali jadwal berganti.
  useEffect(() => {
    setSelectedStatusId(null);
    setReport("");
    setWaOpen(false);
    setWaReport("");
    setWaAppId(null);
    setAdminHebat({
      appId: MEETING_APP.FINANCE,
      company: null,
    });
  }, [scheduleId]);

  // Set default app sesuai admin group jadwal (sekali per jadwal saat detail tiba).
  const appDefaultAppliedRef = useRef(null);
  useEffect(() => {
    if (!scheduleDetail || appDefaultAppliedRef.current === scheduleId) return;
    appDefaultAppliedRef.current = scheduleId;
    const appId = resolveAdminGroupAppId(
      resolveScheduleAdminGroupId(scheduleDetail)
    );
    setAdminHebat((prev) => ({ ...prev, appId }));
  }, [scheduleDetail, scheduleId]);

  const currentStatusId = useMemo(() => {
    if (!scheduleDetail) return null;
    return getScheduleRowStatusId({ ...schedule, ...scheduleDetail });
  }, [scheduleDetail, schedule]);

  // Tentukan opsi dropdown berdasarkan ID status saat ini.
  const { eligible, options } = useMemo(() => {
    if (currentStatusId == null) return { eligible: null, options: [] };
    const id = Number(currentStatusId);
    if (CONFIRMABLE_STATUS_IDS.includes(id)) {
      return {
        eligible: true,
        options: [
          buildStatusOption(statuses, STATUS.DONE, locale),
          buildStatusOption(statuses, STATUS.NO_SHOW, locale),
        ],
      };
    }
    if (id === STATUS.PENAWARAN_ZOOM) {
      return {
        eligible: true,
        options: [
          buildStatusOption(statuses, STATUS.DONE, locale),
          buildStatusOption(statuses, STATUS.CANCELLED, locale),
        ],
      };
    }
    return { eligible: false, options: [] };
  }, [currentStatusId, statuses, locale]);

  // Status saat ini di luar grup yang ditangani → jangan tampilkan modal.
  // Jangan auto-close saat pratinjau WhatsApp terbuka: setelah save, refetch
  // detail membuat status jadi DONE (eligible=false) — tanpa guard ini modal WA
  // akan ikut ter-unmount begitu refetch selesai.
  useEffect(() => {
    if (!isLoading && scheduleDetail && eligible === false && !waOpen) {
      onClose();
    }
  }, [isLoading, scheduleDetail, eligible, onClose, waOpen]);

  // Gagal memuat detail → tutup modal supaya user tidak terkunci
  // (modal non-closable) dan handler bisa menerima pusher berikutnya.
  useEffect(() => {
    if (isError) {
      message.error(t("meeting_finished.load_failed"));
      onClose();
    }
  }, [isError, message, t, onClose]);

  const isSelesaiSelected = Number(selectedStatusId) === STATUS.DONE;

  const reportTooShort =
    isSelesaiSelected && report.trim().length < REPORT_MIN_LENGTH;

  // AdminHebat wajib → perusahaan harus dipilih sebelum bisa simpan.
  const adminHebatActive = isInternal && isSelesaiSelected;
  const adminHebatMissingCompany = adminHebatActive && !adminHebat.company;

  const canSave =
    selectedStatusId != null &&
    !reportTooShort &&
    !adminHebatMissingCompany &&
    !patchStatusMutation.isPending;

  const handleSave = () => {
    if (!canSave || !scheduleId) return;
    patchStatusMutation.mutate(
      {
        id: scheduleId,
        status_id: Number(selectedStatusId),
        ...(isSelesaiSelected ? { report: report.trim() } : {}),
        ...(adminHebatActive && adminHebat.company
          ? { company_id: adminHebat.company.id, app_id: adminHebat.appId }
          : {}),
      },
      {
        onSuccess: () => {
          message.success(t("meeting_finished.saved"));
          // Selesai + ada report → lanjut ke pratinjau WhatsApp; selain itu tutup.
          if (isSelesaiSelected) {
            setWaReport(report.trim());
            setWaAppId(adminHebat.appId);
            setWaOpen(true);
          } else {
            onClose();
          }
        },
        onError: (err) => {
          message.error(
            err?.response?.data?.message || t("meeting_finished.save_failed")
          );
        },
      }
    );
  };

  const open = Boolean(scheduleId) && eligible !== false;

  // Data untuk membuka wizard edit (Reschedule) — sama seperti "Ubah Jadwal".
  const handleReschedule = () => {
    if (!scheduleDetail) return;
    onReschedule?.({
      ...scheduleDetail,
      contact: scheduleDetail.contact ?? null,
      contact_id: resolveScheduleContactId(scheduleDetail),
    });
  };

  // Warna trigger mengikuti status terpilih (sama seperti kolom status di tabel).
  const selectedOption = options.find(
    (o) => Number(o.value) === Number(selectedStatusId)
  );
  const triggerColor = selectedOption?.color ?? null;
  const hasTriggerColor = Boolean(triggerColor);
  const statusCellStyle = {
    "--schedule-status-sel-fg": "#000",
    ...(hasTriggerColor ? { "--schedule-status-sel-bg": triggerColor } : {}),
  };

  const dateLabel = scheduleDetail?.date
    ? dayjs(scheduleDetail.date).format("dddd, MMM D")
    : "";
  const yearLabel = scheduleDetail?.date
    ? dayjs(scheduleDetail.date).format("YYYY")
    : "";
  const timeLabel =
    scheduleDetail?.start_time && scheduleDetail?.end_time
      ? `${dayjs(scheduleDetail.start_time, "HH:mm:ss").format(
          "HH:mm"
        )} – ${dayjs(scheduleDetail.end_time, "HH:mm:ss").format("HH:mm")}`
      : "";

  return (
    <>
    <Modal
      open={open && !waOpen}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={680}
      destroyOnHidden
      title={
        scheduleDetail?.date ? (
          <div className="border-b border-neutral-100 pb-4 -mt-1">
            <p className="mb-0.5 text-xs font-medium uppercase tracking-wide text-neutral-500">
              {yearLabel}
            </p>
            <p className="mb-1 text-2xl font-semibold leading-tight text-neutral-900">
              {dateLabel}
            </p>
            {timeLabel && (
              <p className="mb-0 text-sm text-neutral-600">
                {t("meeting_management.at")}{" "}
                <span className="font-medium text-neutral-800">{timeLabel}</span>
              </p>
            )}
          </div>
        ) : (
          t("meeting_finished.title")
        )
      }
      classNames={{ body: "pt-2" }}
    >
      {isLoading || !scheduleDetail ? (
        <Skeleton active />
      ) : (
        <div className="space-y-6">
          {/* Judul */}
          <div className="flex items-start gap-3 border-b border-neutral-100 pb-5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
              <CheckCircleFilled style={{ fontSize: 24 }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="mb-0.5 text-xl font-bold leading-snug text-neutral-900">
                {t("meeting_finished.intro")}
              </p>
              <p className="mb-0 text-sm text-neutral-500">
                {t("meeting_finished.intro_sub")}
              </p>
            </div>
            {scheduleDetail?.schedule_admin_name && (
              <Tag color="#f80" className="m-0 shrink-0 text-sm">
                {scheduleDetail.schedule_admin_name}
              </Tag>
            )}
          </div>

          {/* Detail kontak */}
          <Descriptions
            column={1}
            size="small"
            bordered
            className="overflow-hidden rounded-lg [&_.ant-descriptions-item-label]:w-[140px] [&_.ant-descriptions-item-label]:bg-neutral-50 [&_.ant-descriptions-item-label]:font-medium"
          >
            <Descriptions.Item label={t("name")}>
              <ContactDetailLink
                contactId={resolveScheduleContactId(scheduleDetail)}
              >
                {scheduleDetail?.contact?.name}
              </ContactDetailLink>
            </Descriptions.Item>
            <Descriptions.Item label={t("company")}>
              <ContactDetailLink
                contactId={resolveScheduleContactId(scheduleDetail)}
              >
                {scheduleDetail?.contact?.company}
              </ContactDetailLink>
            </Descriptions.Item>
            <Descriptions.Item label={t("phone")}>
              {scheduleDetail?.contact?.phone ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("email")}>
              {scheduleDetail?.contact?.email ?? "—"}
            </Descriptions.Item>
          </Descriptions>

          {/* Status meeting */}
          <div className="space-y-2">
            <p className="mb-0 text-sm font-semibold text-neutral-800">
              {t("meeting_finished.status_question")}
            </p>
            <div
              className="schedule-status-select-cell block w-full max-w-full align-middle"
              data-has-color={hasTriggerColor ? "true" : "false"}
              style={statusCellStyle}
            >
              <Select
                variant={hasTriggerColor ? "borderless" : "outlined"}
                allowClear
                size="large"
                className="w-full !text-black"
                popupClassName="ms-schedule-status-select-dropdown"
                styles={{ popup: { root: { padding: 0 } } }}
                suffixIcon={<DownOutlined className="!text-black" />}
                placeholder={t("meeting_finished.status_placeholder")}
                value={selectedStatusId ?? undefined}
                onChange={(val) => setSelectedStatusId(val ?? null)}
              >
                {options.map((o) => {
                  const bg = o.color || "#f0f0f0";
                  return (
                    <Option
                      key={o.value}
                      value={o.value}
                      style={{ backgroundColor: bg, color: "#000" }}
                      className="ms-schedule-status-option rounded-none font-medium !text-black"
                    >
                      {o.label}
                    </Option>
                  );
                })}
              </Select>
            </div>
          </div>

          {/* Hasil meeting (hanya saat Selesai) */}
          {isSelesaiSelected && (
            <div className="space-y-2">
              <p className="mb-0 text-sm font-semibold text-neutral-800">
                {t("meeting_finished.report_question")}
              </p>
              <TextArea
                autoFocus
                value={report}
                onChange={(e) => setReport(e.target.value)}
                rows={5}
                showCount
                maxLength={1000}
                placeholder={t("meeting_finished.report_placeholder")}
              />
              <p
                className={
                  reportTooShort
                    ? "mb-0 text-xs text-red-500"
                    : "mb-0 text-xs text-neutral-400"
                }
              >
                {t("meeting_finished.report_min", { min: REPORT_MIN_LENGTH })}
              </p>
            </div>
          )}

          {/* Kirim laporan ke AdminHebat (khusus internal, saat Selesai) */}
          {isInternal && isSelesaiSelected && (
            <AdminHebatReportFields
              contactId={resolveScheduleContactId(scheduleDetail)}
              value={adminHebat}
              onChange={setAdminHebat}
            />
          )}

          {/* Aksi */}
          <div className="flex items-center justify-between border-t border-neutral-100 pt-4">
            <Button
              size="large"
              icon={<CalendarOutlined />}
              disabled={patchStatusMutation.isPending}
              onClick={handleReschedule}
            >
              {t("meeting_finished.reschedule")}
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              loading={patchStatusMutation.isPending}
              disabled={!canSave}
              onClick={handleSave}
            >
              {t("meeting_finished.save")}
            </Button>
          </div>
        </div>
      )}
    </Modal>
    <MeetingReportWhatsappModal
      open={waOpen}
      scheduleId={scheduleId}
      reportOverride={waReport}
      appId={waAppId}
      onClose={() => {
        setWaOpen(false);
        onClose();
      }}
    />
    </>
  );
};

export default MeetingFinishedModal;
